/**
 * Ce qu'un type de question implique, côté saisie.
 *
 * Séparé du composant : ce sont des fonctions, pas des composants, et les
 * exporter depuis un module de rendu casse le rafraîchissement à chaud. Le
 * pendant serveur de ces règles vit dans `QuestionType` (`domain/value_objects`)
 * — c'est **lui** qui décide, ces fonctions n'existent que pour ne pas afficher
 * un formulaire dont on sait qu'il produirait une saisie refusée.
 */
import type {
  AnswerKeyInput,
  ChoiceInput,
  ExerciseQuestion,
  QuestionType,
} from "@/features/catalog/api/useCatalog";
import { asDocument, type ContentDocument } from "@/shared/richtext/document";

export function hasChoices(type: QuestionType): boolean {
  return type === "single_choice" || type === "multiple_choice" || type === "true_false";
}

export function hasAnswerKeys(type: QuestionType): boolean {
  return type === "short_text" || type === "number";
}

export interface QuestionDraft {
  type: QuestionType;
  /** Énoncé et explication sont de la prose riche (docs/11 §3). */
  prompt: ContentDocument | null;
  explanation: ContentDocument | null;
  choices: ChoiceInput[];
  answerKeys: AnswerKeyInput[];
}

export function emptyDraft(type: QuestionType = "single_choice"): QuestionDraft {
  return {
    type,
    prompt: null,
    explanation: null,
    // Deux propositions d'emblée : une question à choix en demande au moins
    // deux, et faire commencer l'enseignant par un bouton « ajouter » lui
    // ferait découvrir la règle après coup.
    choices: hasChoices(type)
      ? [
          { label: "", is_correct: true },
          { label: "", is_correct: false },
        ]
      : [],
    answerKeys: hasAnswerKeys(type) ? [{ value: "", match_mode: "exact" }] : [],
  };
}

/**
 * Brouillon d'édition d'une question **existante**.
 *
 * Les `id` des propositions sont **repris**, et c'est tout l'enjeu : renvoyés
 * tels quels, ils font que le serveur met la ligne à jour au lieu de la
 * recréer. Les omettre reviendrait à supprimer chaque proposition puis à en
 * créer une nouvelle sous un autre identifiant — donc à rendre illisibles les
 * tentatives qui les citent (phase 5), pour une simple faute de frappe corrigée.
 */
export function draftOf(question: ExerciseQuestion): QuestionDraft {
  return {
    type: question.type,
    prompt: asDocument(question.prompt),
    explanation: question.explanation === null ? null : asDocument(question.explanation),
    choices: question.choices.map((choice) => ({
      id: choice.id,
      label: choice.label,
      is_correct: choice.is_correct,
      feedback: choice.feedback,
    })),
    answerKeys: question.answer_keys.map((key) => ({
      value: key.value,
      match_mode: key.match_mode,
    })),
  };
}
